import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MemberRole, ScanSummary } from '../api/client';
import { isApiError } from '../api/errors';
import { toDisplayError, type DisplayError } from '../api/errorMessages';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import styles from './ScanRow.module.css';

/** Whether a stop attempt ended the scan we intended (`stopped`) or the row is
 * simply stale — already finished/gone (`stale`). The parent announces success
 * only for `stopped`; both trigger a refetch + focus of the row's Status anchor. */
export type StopOutcome = 'stopped' | 'stale';

export interface ScanRowProps {
  scan: ScanSummary;
  /** The project id, for the "View discovery" link (#19). Omitted → no link. */
  projectId?: string;
  /** The signed-in user, for the privacy-preserving initiator label + ownership. */
  userId: string | null;
  /** The caller's role in the current org (currentOrg.role). */
  role: MemberRole | null;
  /** Parent refetches, announces "Scan stopped." (only when `stopped`), and moves
   * focus to this row's Status anchor. */
  onStopped: (scanId: string, outcome: StopOutcome) => void | Promise<void>;
  /** Bubble a server 403 to the top-level assertive alert (parent also refetches). */
  onForbidden: (display: DisplayError, scanId: string) => void;
  /** Bound client call: POST …/scans/:scanId/stop. */
  stopScan: (scanId: string) => Promise<ScanSummary>;
  /** Register/deregister the Status <td> so the parent can move focus to it after
   * the Stop control vanishes. */
  registerStatusRef: (scanId: string, el: HTMLTableCellElement | null) => void;
}

const OTHER_STOP_MESSAGE = 'We couldn’t stop this scan. Please try again.';

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/**
 * Privacy-preserving initiator label (S3, Option b). We NEVER resolve another
 * member's email or surface a raw UUID: a null initiator is the automatic
 * post-verification scan, the current user is "You", and everyone else is the
 * generic "Member".
 */
function initiatorLabel(initiatedByUserId: string | null, currentUserId: string | null): string {
  if (initiatedByUserId === null) return 'Automatic (verification)';
  if (currentUserId !== null && initiatedByUserId === currentUserId) return 'You';
  return 'Member';
}

/**
 * One scan's full table row (#119). Renders the five cells — Status, Started,
 * Initiated by, Completed, Actions — and owns the two-step inline "Stop" confirm
 * (mirrors MemberRow). The Actions cell is ALWAYS present; the Stop control shows
 * only for a stoppable scan the caller may stop. Row-scoped 409/404/other results
 * announce in the row's OWN polite live region; a 403 bubbles to the parent
 * assertive alert; success is the parent's polite status. Accessible names are
 * keyed on the scan's Started time (never an email, UUID, or initiator).
 */
export function ScanRow({
  scan,
  projectId,
  userId,
  role,
  onStopped,
  onForbidden,
  stopScan,
  registerStatusRef,
}: ScanRowProps) {
  const [rowMessage, setRowMessage] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const stopRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const prevConfirming = useRef(false);

  // Two-step focus: opening moves focus to Confirm; closing (cancel/409/404)
  // returns it to the Stop trigger. A success/stale outcome hands focus off to
  // the parent (the Status anchor) once the Stop control unmounts.
  useEffect(() => {
    if (confirming && !prevConfirming.current) {
      confirmRef.current?.focus();
    } else if (!confirming && prevConfirming.current) {
      stopRef.current?.focus();
    }
    prevConfirming.current = confirming;
  }, [confirming]);

  const stoppable = scan.status === 'initiated' || scan.status === 'running';
  const canStop =
    (scan.initiatedByUserId !== null && scan.initiatedByUserId === userId) || role === 'org_admin';
  const showStop = stoppable && canStop;

  // Privacy-safe accessible name, keyed on the Started time (never PII).
  const t = formatTime(scan.createdAt) ?? `#${scan.id.slice(-8)}`;

  const confirmStop = async () => {
    if (busy) return;
    setBusy(true);
    setRowMessage(null);
    try {
      await stopScan(scan.id);
      // The parent refetches (the scan flips to cancelled → the control unmounts),
      // announces "Scan stopped.", and moves focus to the Status anchor.
      void onStopped(scan.id, 'stopped');
    } catch (err) {
      setBusy(false);
      if (isApiError(err) && err.status === 403) {
        setConfirming(false);
        onForbidden(toDisplayError(err), scan.id);
      } else if (isApiError(err) && err.status === 409) {
        setConfirming(false);
        setRowMessage('This scan already finished.');
        void onStopped(scan.id, 'stale');
      } else if (isApiError(err) && err.status === 404) {
        setConfirming(false);
        setRowMessage('This scan no longer exists.');
        void onStopped(scan.id, 'stale');
      } else {
        setConfirming(false);
        setRowMessage(OTHER_STOP_MESSAGE);
        void onStopped(scan.id, 'stale');
      }
    }
  };

  return (
    <tr>
      <td tabIndex={-1} ref={(el) => registerStatusRef(scan.id, el)}>
        <StatusBadge kind="scan" status={scan.status} />
        {scan.status === 'initiated' && scan.scheduledAt ? (
          <div className={styles.scheduled}>
            <span className={styles.scheduledBadge}>Scheduled</span>
            <span className={styles.scheduledTime}>
              for {formatTime(scan.scheduledAt) ?? '—'}
            </span>
          </div>
        ) : null}
      </td>
      <td>{formatTime(scan.createdAt) ?? '—'}</td>
      <td>{initiatorLabel(scan.initiatedByUserId, userId)}</td>
      <td>{scan.completedAt ? (formatTime(scan.completedAt) ?? '—') : '—'}</td>
      <td>
        <div className={styles.actions}>
          {projectId ? (
            <Link to={`/projects/${projectId}/scans/${scan.id}/discovery`}>View discovery</Link>
          ) : null}
          {showStop ? (
            confirming ? (
              <>
                <Button
                  ref={confirmRef}
                  variant="danger"
                  busy={busy}
                  aria-label={
                    busy ? `Stopping scan started ${t}…` : `Confirm stopping scan started ${t}`
                  }
                  onClick={() => void confirmStop()}
                >
                  Confirm
                </Button>
                <Button
                  variant="ghost"
                  aria-label={`Cancel stopping scan started ${t}`}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                ref={stopRef}
                variant="danger"
                aria-label={`Stop scan started ${t}`}
                onClick={() => setConfirming(true)}
              >
                Stop
              </Button>
            )
          ) : (
            <span aria-hidden="true">—</span>
          )}
        </div>
        <div className={styles.rowStatus}>
          <InlineAlert severity="info">{rowMessage}</InlineAlert>
        </div>
      </td>
    </tr>
  );
}
